import { DomainContext } from '../../changes/DomainContext';
import { DoseId } from '../../changes/ids';
import { domainTrace } from '../../diagnostics/domainTrace';
import { StaleReferenceError } from '../../errors/DomainError';
import { DoseMatrix } from '../../patient/doseMatrix';
import { Patient } from '../../patient/patient';
import { StructureSet } from '../../patient/structureset';

/** Lightweight identity passed from UI down to dose commands. */
export interface DoseRef {
    readonly id: DoseId;
}

/**
 * Application-service seam for the *tracked* writes to a {@link DoseMatrix}:
 * export status and aggregate membership. Each is wrapped in
 * `tracker.command()` so it lands as one changeset.
 *
 * Deliberately small. A dose's display settings (scale, normalization,
 * colorwash range) are UI state held in a per-dose UI store, and its
 * prescription targets/objectives are plain attached metadata set at
 * ingest — neither is a tracked mutation, so neither goes through here.
 */
export class DoseService {
    constructor(
        private readonly context: DomainContext,
        private readonly patient: Patient
    ) {}

    public setExported(ref: DoseRef): void {
        domainTrace.service('DoseService.setExported', { id: ref.id });
        this.context.tracker.command(() => this.resolve(ref).setExported());
    }

    // Adds a dose computed in this session, linked to the structure set it is shown with.
    public add(dose: DoseMatrix, structureSet: StructureSet): DoseRef {
        domainTrace.service('DoseService.add', { id: dose.id });
        if (!this.patient.structureSets.includes(structureSet)) {
            throw new StaleReferenceError(
                `Structure set ${structureSet.id} is not part of this patient.`
            );
        }
        this.context.tracker.command(() => {
            dose.linkStructureSet(structureSet);
            this.patient.addDose(dose);
        });
        return { id: dose.id };
    }

    /**
     * Removes the dose from the patient aggregate, cascading the unlink to
     * any plan that referenced it. Unlike structure sets and registrations
     * a dose carries no approval lock, so there is nothing to block on.
     */
    public remove(ref: DoseRef): void {
        domainTrace.service('DoseService.remove', { id: ref.id });
        this.context.tracker.command(() =>
            this.patient.removeDose(this.resolve(ref))
        );
    }

    private resolve(ref: DoseRef): DoseMatrix {
        const dose = this.patient.doses.find((d) => d.id === ref.id);
        if (!dose) {
            throw new StaleReferenceError(`Dose ${ref.id} no longer exists.`);
        }
        return dose;
    }
}
